import { useMemo, useState } from "react";
import VampScopeBar from "./VampScopeBar";
import VampStandingPill from "./VampStandingPill";
import { VampInfoHint } from "./VampTooltip";
import {
  caidReading,
  fmtInt,
  rankForBreakdown,
  type VampMonthContext,
  type VampReading,
} from "./vampStanding";
import type { VampCaid, VampAccount } from "../../../types/vampRates";

interface VampCaidBreakdownProps {
  account: VampAccount;
  ctx: VampMonthContext;
}

const SHOWN = 8;
// min-w matches the row's own fixed columns (472px) plus the CAID column's 140px floor, its
// 5 gaps and its padding, so every row - header included - shares one guaranteed-identical width.
const GRID = "grid grid-cols-[minmax(140px,1fr)_116px_104px_86px_52px_114px] min-w-[706px] items-center gap-x-2.5 overflow-hidden px-[22px]";

export default function VampCaidBreakdown({ account, ctx }: VampCaidBreakdownProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const ranked = useMemo(
    () => rankForBreakdown(account, ctx).map((caid) => ({ caid, reading: caidReading(caid, ctx) })),
    [account, ctx],
  );
  const hidden = ranked.length - SHOWN;
  const rows = moreOpen ? ranked : ranked.slice(0, SHOWN);
  const when = ctx.isCurrent ? "so far this month" : "in this month";
  const transactionsTip = `Settled Visa transactions on this CAID ${when}, the denominator of its VAMP rate.`;

  const renderRow = ({ caid, reading }: { caid: VampCaid; reading: VampReading }) => {
    const { totals, unmatched, scored } = reading;
    return (
      <div key={caid.id} className={`${GRID} border-t border-gray-100 py-2 ${scored ? "bg-gray-25" : ""}`}>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold leading-[19px] text-gray-900">{caid.title}</div>
          <div className="truncate text-[11px] leading-[15px] text-gray-400 tabular-nums">{caid.caid ?? "no MID on file"}</div>
        </div>
        <VampScopeBar events={totals.events} elapsed={ctx.elapsed} size="sm" />
        <span className="text-right text-theme-xs text-gray-500 tabular-nums">{fmtInt(totals.settled)}</span>
        <span className={`text-right text-theme-xs tabular-nums ${scored ? "text-gray-700" : "text-gray-400"}`}>
          {unmatched ? "–" : `${fmtInt(totals.events)} / 1,500`}
        </span>
        <span className={`text-right text-[13px] font-bold leading-[19px] tabular-nums ${reading.rateInk}`}>
          {reading.rateLabel}
        </span>
        <span className="justify-self-end">
          <VampStandingPill standing={reading.standing} />
        </span>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-[22px] pb-2.5 pt-3">
        <span className="text-sm font-semibold text-gray-900">Overview</span>
      </div>
      <div className="overflow-x-auto">
        <div className={`${GRID} bg-gray-50 py-2 text-[11px] font-medium uppercase leading-4 tracking-[0.04em] text-gray-400`}>
          <span>CAID</span>
          <span>Threshold</span>
          <span className="flex items-center justify-end gap-1">
            Transactions
            <VampInfoHint text={transactionsTip} placement="bottom" />
          </span>
          <span className="text-right">Events</span>
          <span className="text-right">Rate</span>
          <span className="text-right">Standing</span>
        </div>
        {rows.map(renderRow)}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          className="flex w-full items-center gap-[7px] rounded-b-[11px] border-t border-gray-100 bg-gray-25 px-[22px] py-[9px] text-left text-theme-xs text-gray-500"
          onClick={() => setMoreOpen(!moreOpen)}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d={moreOpen ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
          </svg>
          {moreOpen ? "Show less" : `${hidden} more CAID${hidden === 1 ? "" : "s"}`}
        </button>
      )}
    </div>
  );
}
